import React from 'react';
import { InfoWindow } from '@react-google-maps/api';
import { Box, Stack, Typography, Divider, Chip, Tooltip } from '@mui/material';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransitFilled';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import { FielderPink, FielderSoft } from '@core/theme/baseTheme';

const BICYCLING = 1;
const MOTORCYCLE = 2;
const DRIVING = 3;
const TRANSIT = 4;
const TRAILER = 5;
const WALKING = 6;
const formatDurationDHMS = (totalSeconds = 0) => {
  if (typeof totalSeconds !== 'number') return String(totalSeconds || '');
  const s = Math.max(0, Math.floor(totalSeconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
};
const formatDistance = (meters = 0) => {
  const m = Math.max(0, Number(meters) || 0);
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(2)} km`;
};
const ellipsis = (str = '', max = 34) =>
  str && str.length > max ? `${str.slice(0, max - 1)}…` : str;
const getModeIcon = (mode) => {
  switch (mode) {
    case BICYCLING:
      return <DirectionsBikeIcon fontSize="small" sx={{ color: FielderPink }} />;
    case MOTORCYCLE:
      return <TwoWheelerIcon fontSize="small" sx={{ color: FielderPink }} />;
    case DRIVING:
      return <DirectionsCarIcon fontSize="small" sx={{ color: FielderPink }} />;
    case TRANSIT:
      return <DirectionsTransitIcon fontSize="small" sx={{ color: FielderPink }} />;
    case TRAILER:
      return <LocalShippingIcon fontSize="small" sx={{ color: FielderPink }} />;
    case WALKING:
      return <DirectionsWalkIcon fontSize="small" sx={{ color: FielderPink }} />;
    default:
      return <DirectionsCarIcon fontSize="small" sx={{ color: FielderPink }} />;
  }
};
const RouteInfoWindow = ({ infoWindow, travelMode, task, agent, closeRoute }) => {
  if (!infoWindow?.position) return null;
  const distance =
    infoWindow.distance ??
    (typeof infoWindow.distanceMeters === 'number'
      ? formatDistance(infoWindow.distanceMeters)
      : '');
  const duration =
    infoWindow.duration ??
    (typeof infoWindow.durationSeconds === 'number'
      ? formatDurationDHMS(infoWindow.durationSeconds)
      : '');
  return (
    <InfoWindow position={infoWindow.position} onCloseClick={closeRoute}>
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {getModeIcon(travelMode)}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, letterSpacing: 0.3, color: FielderPink }}
            >
              {duration}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={distance}
            sx={{
              bgcolor: FielderPink,
              color: FielderSoft,
              height: 22,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Stack>
        <Divider sx={{ borderColor: FielderPink, my: 1 }} />
        <Stack spacing={0.5}>
          {task != null && (
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{task}</Typography>
          )}
          {agent && (
            <Tooltip title={agent}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{ellipsis(agent, 34)}</Typography>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </InfoWindow>
  );
};
export default React.memo(RouteInfoWindow);
